import { mapState } from 'vuex';

const IDLE_THRESHOLD_MS = 10000; // 10초 무활동 시 idle
const IDLE_CHECK_MS = 3000;

export default {
  data() {
    return {
      interval: null,
      isIdle: false,
      isObserving: false,
      lastActivityTs: Date.now(),
      observer: null,
      sectionMap: new Map(), // reviewId -> {visibleStart, totalMs}
      scrollTrackableAttr: 'scroll-track-section',
      changeTrackableAttr: 'change-track-section',
      trackSectionType: null
    };
  },
  computed: {
    ...mapState(['productCode', 'brandCode']),
    ...mapState('reviewSettings', ['laboratorySettings']),
    ...mapState('widget', ['cremaDomainPrefix']),
    cssSelector() {
      return `[${this.trackableAttr}]`;
    },
    trackableAttr() {
      return this.isTrackingScroll
        ? this.scrollTrackableAttr
        : this.changeTrackableAttr;
    },
    isTrackingScroll() {
      return !!document.querySelector(`[${this.scrollTrackableAttr}]`);
    },
    isTrackingChange() {
      return !!document.querySelector(`[${this.changeTrackableAttr}]`);
    }
  },
  watch: {
    isIdle() {
      if (this.isIdle) this.unobserve();
      else this.observe();
    }
  },
  beforeDestroy() {
    ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach(event =>
      window.removeEventListener(event, this.onUserActivity, { passive: true })
    );
    document.removeEventListener('visibilitychange', this.onVisibiltyChange);
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    window.removeEventListener('pagehide', this.sendOnUnload);
  },
  methods: {
    analyze() {
      if (!this.laboratorySettings.enable_vue_widget_analytics) return;

      this.setTrackSectionType();

      if (this.isTrackingScroll) this.observeIntersections();
      else if (this.isTrackingChange) this.observeMutations();
    },
    setTrackSectionType() {
      this.trackSectionType = document
        .querySelector('[track-section-type]')
        ?.getAttribute('track-section-type');
    },
    observeIntersections() {
      if (!('IntersectionObserver' in window)) return;

      ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach(event =>
        window.addEventListener(event, this.onUserActivity, { passive: true })
      );
      document.addEventListener('visibilitychange', this.onVisibiltyChange);

      if (!this.interval)
        this.interval = setInterval(this.checkIdle, IDLE_CHECK_MS);

      // 체류시간 측정
      this.observer = new IntersectionObserver(
        entries => {
          const now = Date.now();
          entries.forEach(entry => {
            const reviewId = entry.target.getAttribute(this.trackableAttr);
            if (!this.sectionMap.has(reviewId))
              this.sectionMap.set(reviewId, { visibleStart: null, totalMs: 0 });
            const meta = this.sectionMap.get(reviewId);
            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              // 50% 이상 화면에 노출 시 측정
              if (
                !meta.visibleStart &&
                !this.isIdle &&
                document.visibilityState === 'visible'
              ) {
                meta.visibleStart = now;
              }
            } else {
              if (meta.visibleStart) {
                meta.totalMs += now - meta.visibleStart;
                meta.visibleStart = null;
              }
            }
          });
        },
        { threshold: [0, 0.5, 1] }
      );
      this.observe();
      window.addEventListener('pagehide', this.sendOnUnload);
    },
    observeMutations() {
      if (!('MutationObserver' in window)) return;

      ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach(event =>
        window.addEventListener(event, this.onUserActivity, { passive: true })
      );
      document.addEventListener('visibilitychange', this.onVisibiltyChange);

      if (!this.interval)
        this.interval = setInterval(this.checkIdle, IDLE_CHECK_MS);

      const target = document.querySelector(`[${this.changeTrackableAttr}]`);
      let reviewId = target.getAttribute(this.changeTrackableAttr);

      this.sectionMap.set(reviewId, { visibleStart: Date.now(), totalMs: 0 });

      // 체류시간 측정
      this.observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === this.changeTrackableAttr
          ) {
            const newReviewId = target.getAttribute(this.changeTrackableAttr);
            const now = Date.now();

            if (this.sectionMap.has(newReviewId)) {
              this.sectionMap.get(newReviewId).visibleStart = now;
            } else {
              this.sectionMap.set(newReviewId, {
                visibleStart: now,
                totalMs: 0
              });
            }
            const meta = this.sectionMap.get(reviewId);
            meta.totalMs += now - (meta.visibleStart || this.lastActivityTs);
            meta.visibleStart = null;

            reviewId = newReviewId;
          }
        });
      });
      this.observe();
      window.addEventListener('pagehide', this.sendOnUnload);
    },
    onUserActivity() {
      this.lastActivityTs = Date.now();
      if (this.isIdle) this.isIdle = false;
    },
    onVisibiltyChange() {
      if (document.visibilityState === 'hidden') this.unobserve();
      else this.observe();
    },
    observe() {
      if (this.isObserving) return;

      if (this.isTrackingScroll) {
        document
          .querySelectorAll(this.cssSelector)
          .forEach(el => this.observer.observe(el));
      } else if (this.isTrackingChange) {
        const target = document.querySelector(`[${this.changeTrackableAttr}]`);
        this.observer.observe(target, {
          attributes: true,
          attributeFilter: [this.changeTrackableAttr]
        });
      }
      this.isObserving = true;
    },
    unobserve() {
      if (!this.isObserving) return;

      this.sectionMap.forEach(meta => {
        if (meta.visibleStart) {
          meta.totalMs += Date.now() - meta.visibleStart;
          meta.visibleStart = null;
        }
      });

      if (this.isTrackingScroll) {
        document
          .querySelectorAll(this.cssSelector)
          .forEach(el => this.observer.unobserve(el));
      } else if (this.isTrackingChange) {
        this.observer.disconnect();
      }
      this.isObserving = false;
    },
    checkIdle() {
      const isFullscreenPopupPopped = !!document.querySelector(
        'iframe[id^="crema-fullscreen-popup"]'
      );
      const isActivityStopped =
        Date.now() - this.lastActivityTs > IDLE_THRESHOLD_MS;

      if (!this.isIdle && (isFullscreenPopupPopped || isActivityStopped))
        this.isIdle = true;
    },
    sendOnUnload() {
      const payload = this.buildPayload();
      const json = JSON.stringify(payload);
      if (this.cremaDomainPrefix === 'l') return;

      try {
        const blob = new Blob([json], { type: 'application/json' });
        navigator.sendBeacon(`/api/${this.brandCode}/analytics`, blob);
      } catch (e) {
        this.api.post('/analytics', { data: JSON.stringify(payload) });
      }
    },
    buildPayload() {
      const sections = [];
      this.sectionMap.forEach((meta, reviewId) => {
        if (meta.visibleStart) {
          meta.totalMs += Date.now() - meta.visibleStart;
          meta.visibleStart = null;
        }
        if (!meta.totalMs) return;

        sections.push({
          type: this.trackSectionType,
          product_code: this.productCode,
          review_id: reviewId,
          visible_ms: Math.round(meta.totalMs)
        });
      });

      return {
        page: location.pathname,
        timestamp: Date.now(),
        sections: sections
      };
    }
  }
};
